import React from 'react';
import { colors } from '../../styles/tokens';
import { fmtInt, fmtVenta } from '../../utils/format';
import { GLOSARIO } from '../../utils/glosario';
import { useEsMovil } from '../../hooks/useEsMovil';

export interface EmbudoProps {
  consultas: number;
  solicitudes: number;
  atendidas: number;
  venta: number;
  negados: number;
  /** Fracción 0..1 (o `null` si no hay consultas): usada para el sub de "Solicitudes a bodega". */
  efect: number | null;
  /** `true` cuando TODAS las filas del periodo traen `paresVendidos` null (Proscai caído): muestra "calculando…"/"N/D" en vez del número. */
  ventaUnavailable: boolean;
  /**
   * `true` mientras sigue pendiente el reintento automático de Venta (HF Fix
   * 3, `IndicadoresScreen` reintenta UNA vez ~15s después del primer fetch
   * frío): muestra `"calculando…"` en vez de `"N/D"`. Default `false` (no
   * rompe a los consumidores/pruebas que no les importa este caso).
   */
  ventaCalculando?: boolean;
  /**
   * `true` cuando el modo del Header NO es «Todas»: las cuatro cifras
   * operativas son de las tiendas del modo, pero la venta es SIEMPRE la de toda
   * la cascada (decisión del usuario, 12-oct). La etapa lo dice para que nadie
   * lea las dos como el mismo universo.
   */
  ventaDeTodoElAmbito?: boolean;
}

interface EtapaProps {
  label: string;
  value: string;
  color: string;
  sub: string;
  /** Disposición de teléfono (ver `useEsMovil`); se recibe por prop para no montar 5 listeners de `matchMedia` en la misma tarjeta. */
  esMovil?: boolean;
}

const Etapa: React.FC<EtapaProps> = ({ label, value, color, sub, esMovil = false }) => (
  <div
    style={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: 5,
      // En móvil la etapa es una celda de la rejilla, y el contenedor ya pone
      // el margen lateral: el padding horizontal propio sólo le robaría ancho
      // a un número de 7 cifras.
      padding: esMovil ? '2px 0' : '2px 10px',
      // `minWidth: 0` SÓLO en móvil: sin él, el ancho mínimo del contenido (el
      // número grande) ensancha la columna de la rejilla y vuelve a sacar la
      // tarjeta de los 390 px. En escritorio se deja el `min-width: auto` de
      // siempre — es lo que hoy impide que las 4 etapas se aplasten en una
      // ventana estrecha, y cambiarlo movería la fila justo por encima del
      // punto de corte.
      minWidth: esMovil ? 0 : undefined,
    }}
  >
    <span
      style={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '.05em',
        textTransform: 'uppercase',
        color: colors.texto.quaternary,
      }}
    >
      {label}
    </span>
    <span
      style={{
        // 31 px es el tamaño del handoff, pero una celda de ~170 px no lo
        // aguanta ni con "198,827" ni con "calculando…". 26 px sigue muy por
        // encima del mínimo legible y evita el desbordamiento lateral.
        fontSize: esMovil ? 26 : 31,
        fontWeight: 800,
        letterSpacing: '-.01em',
        color,
        fontVariantNumeric: 'tabular-nums',
        // Última red: un texto sin espacios ("calculando…") rompe dentro de la
        // celda en vez de empujar la página entera.
        overflowWrap: esMovil ? 'anywhere' : undefined,
      }}
    >
      {value}
    </span>
    <span style={{ fontSize: 11.5, color: colors.texto.tertiary }}>{sub}</span>
  </div>
);

const Chevron: React.FC = () => (
  <div style={{ display: 'flex', alignItems: 'center' }}>
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke={colors.chevron}
      strokeWidth={2.6}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M9 6l6 6-6 6" />
    </svg>
  </div>
);

/**
 * Embudo operativo Global: 4 etapas (Consultas → Solicitudes a bodega →
 * Solicitudes atendidas → Venta (pares)) unidas por chevrons, más un
 * divisor vertical y el bloque de Negados. Recreado pixel-exact de
 * `Tablero Colectivo Moda.dc.html` (líneas 67-77): mismos colores, tamaños
 * (31px/800 los números, 11px uppercase la etiqueta, 11.5px el sub) y misma
 * estructura de `flex`.
 *
 * EN TELÉFONO esa fila no cabe. Medido a 390 px, las etapas caían en x=524,
 * 672 y 830 —fuera de la pantalla— y arrastraban la página de lado. Ahí el
 * embudo pasa a una rejilla de 2×2 con Negados a todo el ancho, debajo del
 * divisor, que se vuelve horizontal. Los chevrons DESAPARECEN en esa
 * disposición: apuntan a la derecha y, al saltar de la etapa 2 a la 3, la
 * flecha señalaría el borde de la pantalla en vez de la siguiente etapa.
 * El orden de lectura no cambia (consultas → solicitudes → atendidas → venta).
 */
const Embudo: React.FC<EmbudoProps> = ({
  consultas,
  solicitudes,
  atendidas,
  venta,
  negados,
  ventaUnavailable,
  ventaCalculando = false,
  ventaDeTodoElAmbito = false,
}) => {
  const esMovil = useEsMovil();

  return (
    <div
      data-testid="embudo-operativo"
      style={{
        marginTop: 11,
        background: colors.card,
        border: `1px solid ${colors.border}`,
        borderRadius: 16,
        padding: esMovil ? '16px 14px' : '20px 20px',
        display: esMovil ? 'grid' : 'flex',
        // `minmax(0, 1fr)` y no `1fr`: el mínimo automático de una columna de
        // rejilla es el ancho de su contenido, así que un número largo la
        // ensancharía y la tarjeta volvería a salirse de la pantalla.
        gridTemplateColumns: esMovil ? 'minmax(0, 1fr) minmax(0, 1fr)' : undefined,
        alignItems: 'stretch',
        gap: esMovil ? 14 : 2,
        boxShadow: '0 1px 2px rgba(20,28,54,.05),0 18px 40px -30px rgba(20,28,54,.55)',
      }}
    >
      {/* Rótulos y ayudas del glosario: los mismos que las tarjetas de Tienda. */}
      <Etapa
        label={GLOSARIO.consultas.rotulo}
        value={fmtInt(consultas)}
        color={colors.naranja}
        sub={GLOSARIO.consultas.nota ?? ''}
        esMovil={esMovil}
      />
      {!esMovil && <Chevron />}
      {/* Ítem 6 (2026-07-28): sin el % de las consultas; sub descriptivo. */}
      <Etapa
        label={GLOSARIO.solicitudes.rotulo}
        value={fmtInt(solicitudes)}
        color={colors.brand}
        sub={GLOSARIO.solicitudes.nota ?? ''}
        esMovil={esMovil}
      />
      {!esMovil && <Chevron />}
      <Etapa
        label={GLOSARIO.atendidas.rotulo}
        value={fmtInt(atendidas)}
        color={colors.verde}
        sub={GLOSARIO.atendidas.nota ?? ''}
        esMovil={esMovil}
      />
      {!esMovil && <Chevron />}
      <Etapa
        label={GLOSARIO.venta.rotulo}
        value={fmtVenta(venta, ventaUnavailable, ventaCalculando)}
        color={colors.azulClaro}
        sub={ventaDeTodoElAmbito ? 'De todas las tiendas del ámbito, sin modo' : 'Cierre con el cliente'}
        esMovil={esMovil}
      />

      <div
        style={
          esMovil
            ? {
                // El divisor vertical de escritorio, tumbado: sigue separando
                // las 4 etapas del bloque de Negados, ahora a lo ancho de las
                // dos columnas.
                gridColumn: '1 / -1',
                height: 1,
                background: colors.ringTrack,
                margin: '2px 0',
              }
            : { width: 1, background: colors.ringTrack, margin: '4px 12px' }
        }
      />

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 5,
          padding: esMovil ? '2px 0' : '2px 8px',
          // 130 px de mínimo son sanos en la fila de escritorio y estorban en
          // una pantalla de 390 px.
          minWidth: esMovil ? 0 : 130,
          // A todo el ancho: es la única cifra que no pertenece a la secuencia
          // del embudo, y así no queda huérfana en media rejilla.
          gridColumn: esMovil ? '1 / -1' : undefined,
        }}
      >
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '.05em',
            textTransform: 'uppercase',
            color: colors.rojo,
          }}
        >
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke={colors.rojo}
            strokeWidth={2.6}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M7 7l10 10M17 10v7h-7" />
          </svg>
          {GLOSARIO.negados.rotulo}
        </span>
        <span
          style={{
            fontSize: esMovil ? 26 : 31,
            fontWeight: 800,
            letterSpacing: '-.01em',
            color: colors.rojo,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {fmtInt(negados)}
        </span>
        <span style={{ fontSize: 11.5, color: colors.texto.tertiary }}>
          {GLOSARIO.negados.nota}
        </span>
      </div>
    </div>
  );
};

export default Embudo;
